import * as React from "react"
import { useTranslation } from "react-i18next"
import { useQuery } from "@tanstack/react-query"
import {
  Badge,
  Button,
  Icon,
  MediaPreview,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@nexus/ui"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { usePermissions } from "@/features/auth/hooks/usePermissions"
import { personaDetailQueryOptions, personaUsageQueryOptions } from "@/lib/queries"
import {
  humanizePersonaIndustry,
  humanizePersonaTitle,
  humanizePersonaSentence,
} from "../../lib/persona-format"
import { resolvePersonaAdMedia } from "../../lib/persona-ad-media"
import { PERSONA_DRAWER_SPEND_FILL_DELAY_MS } from "../../constants/personas.constants"
import { PersonaKpi } from "../PersonaKpi/PersonaKpi"
import { PersonaPortrait } from "../PersonaPortrait/PersonaPortrait"
import { PersonaStaleBadge } from "../PersonaStaleBadge/PersonaStaleBadge"
import { PersonaUsagePanel } from "../PersonaUsagePanel/PersonaUsagePanel"
import { PersonaStatusBadge } from "../PersonaStatusBadge/PersonaStatusBadge"
import { PersonaTypeBadge } from "../PersonaTypeBadge/PersonaTypeBadge"
import { ImagePreviewTrigger } from "@/features/identity-hub/components/dashboard/ImagePreviewTrigger"
import type { Persona } from "../../types/personas.types"

interface PersonaDetailExample {
  id: string
  thumbnailUrl: string
  assetUrl: string
  type: "image" | "video"
  totalDays: number
}

/** `persona.topAds` (top 3 by spend, BQ sync) mapped to the Examples tab's row shape. */
function examplesFromTopAds(persona: Persona): PersonaDetailExample[] {
  return (persona.topAds ?? []).map((ad) => {
    const media = resolvePersonaAdMedia(ad)
    return { id: ad.hash, thumbnailUrl: media.thumbnailUrl, assetUrl: media.assetUrl, type: media.type, totalDays: ad.totalDays }
  })
}

// DESIGN.md drawer-right is 720px by default; the plan calls for a wider ~960px
// panel here so the Examples tab's creative table has room to breathe.
const DRAWER_MAX_WIDTH_CLASS = "sm:max-w-[960px]"

export interface PersonaDetailDrawerProps {
  /** null closes the drawer; a non-empty id opens it and drives the detail fetch. */
  personaId: string | null
  totalSpend: number
  onOpenChange: (open: boolean) => void
  /** Surfaces the same primary CTA as `PersonaCard` in the footer — omit to hide the footer entirely. */
  onCreateOpportunitySet?: (persona: Persona) => void
}

/** Section label shared by the drawer's Details panels — quiet, tracked, and the same
 * size everywhere so the two-column grid reads as one row of peers. */
const SECTION_LABEL_CLASS = "text-[11px] font-medium uppercase tracking-wide text-muted-foreground"

function ProfileList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <div className="flex flex-col gap-2">
      <p className={SECTION_LABEL_CLASS}>{label}</p>
      <ul className="flex flex-col gap-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
            <span
              aria-hidden
              className="mt-[7px] size-1 shrink-0 rounded-full bg-muted-foreground/50"
            />
            <span className="leading-snug">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function DetailDrawerSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Skeleton className="size-10 shrink-0 rounded-full" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-4 w-2/5 rounded" />
          <Skeleton className="h-3 w-1/5 rounded" />
        </div>
      </div>
      <Skeleton className="h-16 w-full rounded-md" />
      <Skeleton className="h-9 w-56 rounded-md" />
      <Skeleton className="h-14 w-full rounded-md" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-24 w-full rounded-md" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
    </div>
  )
}

export function PersonaDetailDrawer({
  personaId,
  totalSpend,
  onOpenChange,
  onCreateOpportunitySet,
}: PersonaDetailDrawerProps) {
  const { t } = useTranslation()
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const perms = usePermissions()
  // Usage names the tenant's opportunity sets, so it has its own grant. Without it
  // the tab is absent rather than empty — an empty tab reads as "no sets exist".
  const canReadUsage = perms.PERSONAS_GET_USAGE?.isOn === true
  const open = personaId !== null

  const { data: persona, isLoading, isError } = useQuery(
    personaDetailQueryOptions(activeTenantId, personaId ?? ""),
  )

  const {
    data: usage,
    isLoading: isUsageLoading,
    isError: isUsageError,
  } = useQuery(
    personaUsageQueryOptions(activeTenantId, personaId ?? "", { enabled: canReadUsage }),
  )

  const examples = React.useMemo(() => (persona ? examplesFromTopAds(persona) : []), [persona])

  // One source of truth for the footer CTA and the "Used in" empty state that points
  // at it — a rejected persona cannot seed a new set.
  const canCreateSet = !!onCreateOpportunitySet && persona?.status !== "REJECTED"

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <SheetContent className={DRAWER_MAX_WIDTH_CLASS}>
        {isLoading && (
          <SheetBody>
            <DetailDrawerSkeleton />
          </SheetBody>
        )}

        {!isLoading && isError && (
          <SheetBody>
            <p className="text-sm text-muted-foreground">
              {t("personas.detail.loadFailed", "Failed to load this user persona")}
            </p>
          </SheetBody>
        )}

        {!isLoading && !isError && persona && (
          <>
            <SheetHeader>
              <div className="flex items-start gap-3">
                <ImagePreviewTrigger
                  src={persona.avatarUrl}
                  alt={humanizePersonaTitle(persona.title)}
                  className="group/avatar rounded-full"
                >
                  <span className="relative block transition-transform duration-200 ease-in-out motion-safe:group-hover/avatar:scale-110">
                    <PersonaPortrait
                      avatarUrl={persona.avatarUrl}
                      personalityKey={persona.personalityKey}
                      title={humanizePersonaTitle(persona.title)}
                      loading="eager"
                      className="ring-1 ring-border transition-shadow duration-200 ease-in-out group-hover/avatar:ring-2 group-hover/avatar:ring-primary/50 motion-reduce:transition-none"
                    />
                    {persona.avatarUrl && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-foreground/50 opacity-0 transition-opacity duration-200 ease-in-out group-hover/avatar:opacity-100 motion-reduce:transition-none"
                      >
                        <Icon name="maximize-2" size="xs" className="text-background" />
                      </span>
                    )}
                  </span>
                </ImagePreviewTrigger>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <SheetTitle>{humanizePersonaTitle(persona.title)}</SheetTitle>
                    {persona.staledAt !== null && <PersonaStaleBadge />}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <PersonaStatusBadge status={persona.status} />
                    <PersonaTypeBadge type={persona.type} isMainBrand={persona.isMainBrand} />
                    {persona.industry && (
                      <Badge variant="slate">{humanizePersonaIndustry(persona.industry)}</Badge>
                    )}
                  </div>
                </div>
              </div>
            </SheetHeader>

            <SheetBody className="flex flex-col gap-5">
              <PersonaKpi
                spend={persona.spend}
                totalSpend={totalSpend}
                adCount={persona.adCount}
                fillDelayMs={PERSONA_DRAWER_SPEND_FILL_DELAY_MS}
              />

              <Tabs defaultValue="details">
                <TabsList variant="line">
                  <TabsTrigger value="details">{t("personas.detail.tabDetails", "Details")}</TabsTrigger>
                  <TabsTrigger value="examples" badge={examples.length > 0 ? examples.length : undefined}>
                    {t("personas.detail.tabExamples", "Examples")}
                  </TabsTrigger>
                  {canReadUsage && (
                    <TabsTrigger
                      value="usage"
                      badge={usage && usage.setCount > 0 ? usage.setCount : undefined}
                    >
                      {t("personas.detail.tabUsedIn", "Used in")}
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* Wide (960px) panel: pains and motivations sit side by side rather than in
                    one narrow stacked column, and the Who paragraph is capped at prose width —
                    a single 900px-wide text column blows past comfortable line-length
                    (DESIGN.md typography §5). */}
                <TabsContent value="details" className="flex flex-col gap-5 pt-4">
                  {persona.who && (
                    <div className="flex items-start gap-2.5 rounded-md border border-border/50 bg-muted/30 p-3.5">
                      <Icon name="users" size="sm" className="mt-0.5 shrink-0 text-muted-foreground/60" />
                      <div className="flex flex-col gap-1.5">
                        <p className={SECTION_LABEL_CLASS}>{t("personas.card.who", "Who")}</p>
                        <p className="max-w-prose text-sm leading-relaxed text-foreground">{persona.who}</p>
                      </div>
                    </div>
                  )}

                  {(persona.pains.length > 0 || persona.motivations.length > 0) && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <ProfileList
                        label={t("personas.detail.allPains", "Pains")}
                        items={persona.pains.map(humanizePersonaSentence)}
                      />
                      <ProfileList
                        label={t("personas.detail.allMotivations", "Motivations")}
                        items={persona.motivations.map(humanizePersonaSentence)}
                      />
                    </div>
                  )}

                </TabsContent>

                <TabsContent value="examples" className="pt-4">
                  {examples.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border/60 py-12 text-center">
                      <Icon name="image" size="lg" className="text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">
                        {t("personas.detail.noExamples", "No matched creatives yet")}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-md border border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-24">
                              {t("personas.detail.examplesPreview", "Preview")}
                            </TableHead>
                            <TableHead>{t("personas.detail.examplesFormat", "Format")}</TableHead>
                            <TableHead>{t("personas.detail.examplesLiveHeader", "Days live")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {examples.map((example) => (
                            <TableRow key={example.id}>
                              <TableCell>
                                <MediaPreview
                                  src={example.assetUrl}
                                  thumbnailSrc={example.thumbnailUrl}
                                  type={example.type}
                                  alt={persona.title}
                                  className="aspect-[4/5] w-16 overflow-hidden rounded bg-muted"
                                />
                              </TableCell>
                              <TableCell>
                                {example.type === "video"
                                  ? t("personas.detail.examplesFormatVideo", "Video")
                                  : t("personas.detail.examplesFormatImage", "Image")}
                              </TableCell>
                              <TableCell>
                                {t("personas.detail.examplesLiveDays", "{{days}}d", {
                                  days: Math.round(example.totalDays),
                                })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>

                {canReadUsage && (
                  <TabsContent value="usage" className="pt-4">
                    <PersonaUsagePanel
                      usage={usage}
                      isLoading={isUsageLoading}
                      isError={isUsageError}
                      canCreateSet={canCreateSet}
                    />
                  </TabsContent>
                )}
              </Tabs>
            </SheetBody>

            {canCreateSet && onCreateOpportunitySet && (
              <SheetFooter className="justify-end">
                <Button iconStart="plus" onClick={() => onCreateOpportunitySet(persona)}>
                  {t("personas.card.createOpportunitySet", "Create Opportunity Set")}
                </Button>
              </SheetFooter>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
